import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteNames } from '@/router/router.types';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useActions } from '@hooks/useActions';
import { useAppDispatch } from '@hooks/useAppDispatch';
import './Navbar.scss';
import logo from '@brand/logo-white.svg';
import Button from '@srm/ui/button';
import {
    MdAddAPhoto,
    MdDarkMode,
    MdEditNotifications,
    MdOutlineAssignmentInd,
    MdGroupAdd,
    MdSettings,
    MdSupervisorAccount,
} from 'react-icons/md';
import { IListElement } from '@srm/ui/types/IListElement';
import { IOption } from '@srm/ui/types/IOption';
import { useUi } from '@srm/ui/provider';
import Header, { HeaderBrand } from '@srm/ui/shell/Header';
import { OnboardingHint, UserMenuButton, userMenuItem } from '@srm/ui/shell/UserMenu';
import UserAvatar from '@molecules/UserAvatar/UserAvatar';
import { AVATAR_SIZES } from '@srm/ui/avatar';
import { useAvatarSrc, invalidateAvatarCache } from '@molecules/UserAvatar/avatarUrl';
import { invalidateAvatarMeta } from '@molecules/UserAvatar/useAvatarMeta';
import AvatarEditorModal from '@srm/ui/shell/AvatarEditorModal';
import Modal from '@srm/ui/modal';
import { UserService } from '@services/UserService';
import { bumpAvatarVersion } from '@/store/reducers/UserAvatarReducer';
import Select from '@srm/ui/select';
import { IAddressBookItem } from '@models/addressbook/IAddressBookItem';
import Preloader from '@srm/ui/preloader';
import { IDebugSettings } from '@models/IDebugSettings';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { ConfigService } from '@/configuration/services/configService';
import { canSeeDebugTools } from '@/utils/debug/canSeeDebugTools';
import GlobalSearchInput from '@molecules/GlobalSearchInput';
import { ICustomUserSettings } from '@models/IUser';
import { auth } from '@/auth';
import ChatAIForm from '@molecules/ChatAI/Form/ChatAIForm';
import { useChatAIStatus } from '@/hooks/useChatAIStatus';
import Tooltip from '@srm/ui/tooltip';
import { AiBotIcon, MdAISparkles } from '@icons';
import HelpMenu from './HelpMenu/HelpMenu';
import ToolsMenu from './ToolsMenu/ToolsMenu';
import UserMenuDialog from './UserMenuDialog/UserMenuDialog';
import NotificationDropdown from './NotificationDropdown/NotificationDropdown';
import NotificationBell from './NotificationBell/NotificationBell';
import { useBellJobFx } from './NotificationBell/useBellJobFx';
import { useBellLanding } from './NotificationBell/useBellLanding';
import BellJobHint from './NotificationBell/BellJobHint';
import JobLiveAnnouncer from './JobLiveAnnouncer/JobLiveAnnouncer';
import { useJobOutcomeSignal } from './useJobOutcomeSignal';
import { jobOutcomeToast } from './NotificationToast/jobOutcomeToast';
import { NAVBAR_DROPDOWN_VERTICAL_OFFSET } from './constants';
import { NotificationsService } from '@services/NotificationsService';
import { notificationHub } from '@/signalr/hubs/NotificationHub';
import NotificationToast from './NotificationToast/NotificationToast';
import { ToastNotification } from './NotificationToast/NotificationToast.types';
import { snoozeNotificationToReminder } from '@utils/snoozeNotification';
import { useNavbarOnboarding } from './useNavbarOnboarding';
import { useScreenShareActive, useScreenShareAvailable } from '@providers/ScreenShareProvider/ScreenShareContext';
import { backgroundJobsStore } from '@services/backgroundJobs/BackgroundJobsStore';
import { useHasActiveJobs, useJobDownloads } from '@services/backgroundJobs/useBackgroundJobs';
import { isJobActive } from '@services/backgroundJobs/backgroundJobsReducer';
import { createJobActionHandlers, runJobAction } from '@services/backgroundJobs/backgroundJobKinds';
import { backgroundJobTargetId } from '@utils/notificationTarget';
import type { INotification } from '@models/INotification';

// Модалка «Отложить» тянет DevExtreme DataGrid (~2 MB vendor-чанк) — грузим
// лениво по факту открытия, чтобы не тащить его в стартовый водопад шапки.
const MailReminderExecutorModal = React.lazy(() =>
    import('@organisms/actions/docExecutors/MailReminder/MailReminderExecutorModal').then((m) => ({
        default: m.MailReminderExecutorModal,
    })),
);

/** Сколько живёт превью до авто-скрытия, мс. */
const TOAST_DISMISS_MS = 15_000;
/** Максимум превью в стопке. */
const TOAST_MAX = 6;
/** Сколько свежих непрочитанных тянуть по сигналу о новом уведомлении. */
const TOAST_PREVIEW_TAKE = 4;
type INotificationPreview = Pick<INotification, 'tag' | 'targetObjectKey' | 'targetObjectType'>;

/**
 * Превью уведомления об итоге задачи не нужно, если итог эта вкладка уже показала (тост, окно задачи) или вот-вот
 * покажет: задача у неё числится идущей — её тост придёт, как только клиент задач узнает об окончании; скрытая
 * вкладка отложила сигнал об итоге до возврата.
 */
const isJobOutcomeCoveredHere = (
    notification: INotificationPreview,
    isDeferred: (jobId: string) => boolean,
): boolean => {
    const jobId = backgroundJobTargetId(notification);
    if (!jobId) return false;
    if (backgroundJobsStore.wasAnnounced(jobId) || isDeferred(jobId)) return true;
    const known = backgroundJobsStore.getJob(jobId);
    if (!known || !isJobActive(known)) return false;
    void backgroundJobsStore.refreshJob(jobId);
    return true;
};

const bellLabel = (unread: number, hasRunningJob: boolean, failureMark: boolean): string =>
    [
        'Уведомления',
        unread > 0 && `непрочитанных: ${unread > 99 ? 'больше 99' : unread}`,
        hasRunningJob && 'идёт фоновая задача',
        failureMark && 'фоновая задача завершилась с ошибкой',
    ]
        .filter(Boolean)
        .join(', ');

const Navbar: FC = () => {
    const [chatAIState, setChatAIState] = useState<'closed' | 'expanded' | 'minimized'>('closed');
    const [showNotificationDropdown, setShowNotificationDropdown] = useState<boolean>(false);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [toastItems, setToastItems] = useState<ToastNotification[]>([]);
    const [snoozeCustom, setSnoozeCustom] = useState<{ docKey: string; text: string } | null>(null);
    const [dropdownDetailRequest, setDropdownDetailRequest] = useState<{ id: string } | null>(null);
    const [dropdownRefreshSignal, setDropdownRefreshSignal] = useState<number>(0);
    const [isBellRinging, setIsBellRinging] = useState<boolean>(false);
    const [bellAnimationKey, setBellAnimationKey] = useState<number>(0);
    const notificationButtonElement = useRef<HTMLDivElement | null>(null);
    const userMenuButtonElement = useRef<HTMLDivElement | null>(null);
    // Переход из UiProvider стабилен: useNavigate роутера перерисовывал бы шапку на каждом переходе.
    const { navigate } = useUi();
    const user = useTypedSelector((state) => state.auth.user);
    const avatarVersion = useTypedSelector((state) => state.userAvatar.version);
    // ?v= для своего аватара: основной источник — photoVersion из auth-стейта (immutable-кэш,
    // переживает reload). Локальный bump перебивает его лишь как немедленный cache-bust сразу
    // после смены своего фото; после reload bump=0 и снова используется photoVersion.
    const effectiveAvatarVersion = avatarVersion || user.photoVersion;
    const currentAvatarSrc = useAvatarSrc(String(user.id), user.hasImage, effectiveAvatarVersion);
    const { logout } = useActions();
    const dispatch = useAppDispatch();
    const [avatarEditorOpen, setAvatarEditorOpen] = useState<boolean>(false);
    const showNotificationDropdownRef = useRef<boolean>(false);
    const toastDismissTimerRef = useRef<number | null>(null);
    const toastPausedRef = useRef<boolean>(false);
    const shownToastIdsRef = useRef<Set<string>>(new Set());
    const bellRingTimerRef = useRef<number | null>(null);
    const hasRunningJob = useHasActiveJobs();
    const jobDownloads = useJobDownloads();
    const bellJobFx = useBellJobFx(user.id);

    const [isOrgEditMode, setIsOrgEditMode] = useState<boolean>();
    const [isSavingDepartment, setIsSavingDepartment] = useState<boolean>();
    const [selectedDep, setSelectedDep] = useState<IOption | undefined>();
    const [departmentsList, setDepartmentsList] = useState<IOption[]>([]);
    // Организация, показанная при загрузке списка: правка — только выбор другой
    const shownDepRef = useRef<unknown>(undefined);
    const { getCurrentUser } = useActions();

    const config = ConfigService.get();

    const handleSaveAvatar = useCallback(
        async (file: File) => {
            await UserService.setAvatar(file);
            // Сбрасываем кэши аватара текущего юзера (meta + data:-URL по ключу), чтобы его
            // миниатюра в гридах/hover обновилась без перезагрузки — как в UserProfile.
            invalidateAvatarMeta(String(user.id));
            invalidateAvatarCache(String(user.id));
            dispatch(bumpAvatarVersion());
            getCurrentUser(auth.getAccessToken() ?? '');
        },
        [dispatch, getCurrentUser, user.id],
    );

    const handleRemoveAvatar = useCallback(async () => {
        await UserService.deleteAvatar();
        invalidateAvatarMeta(String(user.id));
        invalidateAvatarCache(String(user.id));
        dispatch(bumpAvatarVersion());
        getCurrentUser(auth.getAccessToken() ?? '');
    }, [dispatch, getCurrentUser, user.id]);

    const fetchUnreadCount = useCallback(() => {
        NotificationsService.getUnreadCount()
            .then((res) => setUnreadCount(res.data))
            .catch(() => {});
    }, []);

    // Локальная мутация в дропдауне (прочтение/удаление/откладывание): при подключённом SignalR
    // бейдж обновит эхо NotificationsChanged — лишний GET /unreadCount не нужен. При обрыве
    // канала дотягиваем счётчик сами.
    const handleLocalMutation = useCallback(() => {
        if (!notificationHub.isConnected) {
            fetchUnreadCount();
        }
    }, [fetchUnreadCount]);

    const refreshNotificationsOnVisibility = useCallback(() => {
        fetchUnreadCount();

        if (showNotificationDropdownRef.current) {
            setDropdownRefreshSignal((prev) => prev + 1);
        }
    }, [fetchUnreadCount]);

    // Тряска + расходящиеся круги колокольчика на новое уведомление (перезапуск с нуля сменой key).
    const triggerBellFx = useCallback(() => {
        if (bellRingTimerRef.current !== null) {
            window.clearTimeout(bellRingTimerRef.current);
        }
        setBellAnimationKey((k) => k + 1);
        setIsBellRinging(true);
        // длиннее самой долгой CSS-анимации (ring №2 = 1.8s + 0.6s delay), чтобы класс не снялся раньше
        bellRingTimerRef.current = window.setTimeout(() => {
            setIsBellRinging(false);
            bellRingTimerRef.current = null;
        }, 2500);
    }, []);

    // ── Очередь всплывающих превью у колокольчика ──
    const clearToastDismiss = useCallback(() => {
        if (toastDismissTimerRef.current !== null) {
            window.clearTimeout(toastDismissTimerRef.current);
            toastDismissTimerRef.current = null;
        }
    }, []);

    const scheduleToastDismiss = useCallback(() => {
        clearToastDismiss();
        if (toastPausedRef.current) return;
        toastDismissTimerRef.current = window.setTimeout(() => {
            setToastItems([]);
            toastDismissTimerRef.current = null;
        }, TOAST_DISMISS_MS);
    }, [clearToastDismiss]);

    // Наведение на стопку / открытое меню «Отложить» — пауза авто-скрытия.
    const handleToastHover = useCallback(
        (paused: boolean) => {
            toastPausedRef.current = paused;
            if (paused) clearToastDismiss();
            else scheduleToastDismiss();
        },
        [clearToastDismiss, scheduleToastDismiss],
    );

    const removeToast = useCallback((id: string) => {
        setToastItems((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const clearToasts = useCallback(() => {
        clearToastDismiss();
        setToastItems([]);
    }, [clearToastDismiss]);

    const enqueueToasts = useCallback(
        (fresh: ToastNotification[]) => {
            if (fresh.length === 0) return;
            setToastItems((prev) =>
                [...fresh, ...prev.filter((p) => !fresh.some((f) => f.id === p.id))].slice(0, TOAST_MAX),
            );
            scheduleToastDismiss();
        },
        [scheduleToastDismiss],
    );

    // Итог задачи, которого пользователь не видел: сцена колокольчика и превью итога в стопке у него. Уведомление в
    // ленту сервер пришлёт сам; его превью тогда не нужно (isJobOutcomeCoveredHere).
    const { isDeferred: isJobOutcomeDeferred } = useJobOutcomeSignal(user.id, {
        isDropdownOpen: () => showNotificationDropdownRef.current,
        play: bellJobFx.play,
        showPreview: (job) => enqueueToasts([jobOutcomeToast(job, Date.now())]),
    });

    // По сигналу о новом уведомлении дотягиваем превью (id/subject/tag) через дропдаун-эндпоинт:
    // realtime-канал несёт только счётчик, тема/тег лежат в blob. Показываем только свежие и не дубли.
    // Звонок — вместе с превью: если все свежие — итоги задач, о которых вкладка уже сообщила, второй раз не звоним.
    const pullToastPreviews = useCallback(() => {
        NotificationsService.fetchDropdownPage(0, TOAST_PREVIEW_TAKE, 'unread')
            .then((page) => {
                const unseen = page.data.filter((n) => !n.read && !shownToastIdsRef.current.has(n.id));
                unseen.forEach((n) => shownToastIdsRef.current.add(n.id));
                const fresh = unseen
                    .filter((n) => !isJobOutcomeCoveredHere(n, isJobOutcomeDeferred))
                    .sort((a, b) => new Date(a.sendedAt).getTime() - new Date(b.sendedAt).getTime());
                if (unseen.length > 0 && fresh.length === 0) return;
                triggerBellFx();
                if (fresh.length === 0) return;
                fresh.forEach((n) => {
                    const jobId = backgroundJobTargetId(n);
                    if (jobId) backgroundJobsStore.markAnnounced(jobId);
                });
                enqueueToasts(
                    fresh.map((n) => ({
                        id: n.id,
                        subject: n.subject,
                        tag: n.tag,
                        targetObjectKey: n.targetObjectKey,
                        targetObjectType: n.targetObjectType,
                        receivedAt: Date.now(),
                    })),
                );
            })
            .catch(triggerBellFx);
    }, [enqueueToasts, triggerBellFx, isJobOutcomeDeferred]);

    // Клик по миниатюре — открыть её предпросмотр прямо в дропдауне уведомлений (не переход на документ).
    const handleToastOpen = useCallback(
        (item: ToastNotification) => {
            removeToast(item.id);
            // У итога задачи страницы уведомления нет — колокольчик откроется на разделе задач с его строкой.
            setDropdownDetailRequest(item.job ? null : { id: item.id });
            setShowNotificationDropdown(true);
        },
        [removeToast],
    );

    // Клик по «+ ещё N в колокольчике» — открыть дропдаун со всеми уведомлениями.
    const handleToastShowAll = useCallback(() => {
        clearToasts();
        setDropdownDetailRequest(null);
        setShowNotificationDropdown(true);
    }, [clearToasts]);

    const handleToastMarkRead = useCallback(
        (item: ToastNotification) => {
            removeToast(item.id);
            NotificationsService.markAsRead([item.id])
                .then(fetchUnreadCount)
                .catch(() => {});
        },
        [removeToast, fetchUnreadCount],
    );

    // Пресет: создаём напоминание на дату + помечаем прочитанным (общий helper).
    const handleToastSnoozePreset = useCallback(
        (item: ToastNotification, date: Date) => {
            removeToast(item.id);
            snoozeNotificationToReminder(item.id, item.subject, date)
                .then(fetchUnreadCount)
                .catch(() => {});
        },
        [removeToast, fetchUnreadCount],
    );

    // «Выбрать время…» — прежний путь через полноценное окно.
    const handleToastSnoozeCustom = useCallback(
        (item: ToastNotification) => {
            removeToast(item.id);
            NotificationsService.getBoundDocumentKey(item.id)
                .then((res) => {
                    if (res.data) setSnoozeCustom({ docKey: res.data, text: item.subject });
                })
                .catch(() => {});
        },
        [removeToast],
    );

    // SignalR: real-time уведомления через PostgreSQL LISTEN/NOTIFY
    useEffect(() => {
        setUnreadCount(0);

        if (!user.id) return;

        fetchUnreadCount();

        // Засеять уже существующие непрочитанные — тостом показываем только новые (после монтирования).
        NotificationsService.fetchDropdownPage(0, 20, 'unread')
            .then((page) => page.data.forEach((n) => shownToastIdsRef.current.add(n.id)))
            .catch(() => {});

        notificationHub.start().catch(() => {});

        // Новое уведомление (из БД) — обновить badge + проиграть эффекты колокольчика
        // и плашки + сбросить dropdown. Эффекты перезапускаются с нуля при каждом событии.
        const unsubNew = notificationHub.onNewNotification((unreadCount) => {
            if (typeof unreadCount === 'number') {
                setUnreadCount(unreadCount);
            } else {
                fetchUnreadCount();
            }
            if (showNotificationDropdownRef.current) {
                triggerBellFx();
            } else {
                pullToastPreviews();
            }
            setDropdownRefreshSignal((prev) => prev + 1);
        });

        // Прочтение/удаление — badge + soft refresh dropdown
        // (soft refresh не очищает список и не показывает loader)
        const unsubChanged = notificationHub.onNotificationsChanged((unreadCount) => {
            if (typeof unreadCount === 'number') {
                setUnreadCount(unreadCount);
            } else {
                fetchUnreadCount();
            }
            setDropdownRefreshSignal((prev) => prev + 1);
        });

        // Fallback — только когда SignalR не подключён
        const interval = setInterval(() => {
            if (document.visibilityState === 'visible' && !notificationHub.isConnected) {
                refreshNotificationsOnVisibility();
            }
        }, 60_000);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshNotificationsOnVisibility();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            unsubNew();
            unsubChanged();
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (toastDismissTimerRef.current !== null) {
                window.clearTimeout(toastDismissTimerRef.current);
                toastDismissTimerRef.current = null;
            }
            if (bellRingTimerRef.current !== null) {
                window.clearTimeout(bellRingTimerRef.current);
                bellRingTimerRef.current = null;
            }
            notificationHub.stop();
        };
    }, [user.id, fetchUnreadCount, refreshNotificationsOnVisibility, triggerBellFx, pullToastPreviews]);

    const jobActionHandlers = useMemo(
        () =>
            createJobActionHandlers({
                store: backgroundJobsStore,
                close: () => setShowNotificationDropdown(false),
                navigate,
            }),
        [navigate],
    );

    // Фоновые задачи пользователя: колокольчик крутит кольцо, пока задача идёт, и сообщает об итоге.
    useEffect(() => {
        if (!user.id) return undefined;
        backgroundJobsStore.start();
        return () => backgroundJobsStore.stop();
    }, [user.id]);

    const handleToastJobAction = useCallback(
        (item: ToastNotification) => {
            const { job } = item;
            if (!job?.action) return;
            removeToast(item.id);
            runJobAction(job.action, job.jobId, jobActionHandlers, job.kind);
        },
        [removeToast, jobActionHandlers],
    );

    const { clearFailureMark } = bellJobFx;
    useEffect(() => {
        if (showNotificationDropdown) clearFailureMark();
    }, [showNotificationDropdown, clearFailureMark]);

    // Задача ушла в фон (flyToBell): колокольчик ловит её и первые разы подсказывает, где следить за ходом.
    const bellLanding = useBellLanding({
        userId: user.id,
        targetRef: notificationButtonElement,
        dropdownOpen: showNotificationDropdown,
        toastsShown: toastItems.length > 0,
        play: bellJobFx.play,
    });
    const openFromBellHint = useCallback(() => {
        clearToasts();
        setDropdownDetailRequest(null);
        setShowNotificationDropdown(true);
    }, [clearToasts]);

    useEffect(() => {
        if (isOrgEditMode) {
            UserService.getDepsToSelect().then((deps) => {
                let depOptions = deps.data.map((x: IAddressBookItem) => ({
                    label: x.name,
                    value: x.key,
                })) as IOption[];
                const shown = depOptions.find((x) => x.value == user.departmentId);
                shownDepRef.current = shown?.value;
                setDepartmentsList(depOptions);
                setSelectedDep(shown);
            });
        }
    }, [isOrgEditMode]);

    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [helpMenuOpen, setHelpMenuOpen] = useState(false);
    // Только признаки показа: полный контекст меняется с каждым сообщением чата и статусом связи.
    const screenShareActive = useScreenShareActive();
    const screenShareAvailable = useScreenShareAvailable();
    const { activeHint, activeStep, hintOpen, dismissHint } = useNavbarOnboarding({
        userId: user.id,
        hasImage: user.hasImage,
        menuOpen: showMenu,
        helpMenuOpen,
        screenShareAvailable,
        suppressed: screenShareActive,
    });

    const dismissAvatarHint = useCallback(() => {
        dismissHint('avatar');
    }, [dismissHint]);

    const dismissThemeHint = useCallback(() => {
        dismissHint('theme');
    }, [dismissHint]);

    const closeUserMenu = useCallback(() => {
        // Закрытие меню завершает его второй шаг: хинт не будет возвращаться при
        // каждом следующем открытии, даже если меню закрыли до автотаймера. Во время показа
        // подсказок не было — и завершать нечего.
        if (activeHint && activeHint !== 'screenShare' && activeStep === 1 && !screenShareActive) {
            dismissHint(activeHint);
        }
        setShowMenu(false);
    }, [activeHint, activeStep, dismissHint, screenShareActive]);

    // То же для «Помощь»: закрытое меню завершает подсказку про показ экрана, показанную в нём.
    const onHelpMenuOpenChange = useCallback(
        (opened: boolean) => {
            if (!opened && activeHint === 'screenShare' && activeStep === 1 && !screenShareActive) {
                dismissHint('screenShare');
            }
            setHelpMenuOpen(opened);
        },
        [activeHint, activeStep, dismissHint, screenShareActive],
    );

    const dismissScreenShareHint = useCallback(() => {
        dismissHint('screenShare');
    }, [dismissHint]);

    const toggleUserMenu = useCallback(() => {
        if (showMenu) closeUserMenu();
        else setShowMenu(true);
    }, [closeUserMenu, showMenu]);

    // Клик по подсказке аватара открывает редактор фото и закрывает онбординг (следующий шаг не показываем).
    const openAvatarEditor = useCallback(() => {
        setAvatarEditorOpen(true);
        dismissAvatarHint();
    }, [dismissAvatarHint]);

    const settingsItem = userMenuItem({
        value: 'user-settings',
        label: 'Профиль и безопасность',
        icon: <MdSettings size="24" />,
        onSelect: () => void navigate(RouteNames.SETTINGS),
    });
    const usersActivationItem = userMenuItem({
        value: 'users-activation',
        label: 'Активация пользователей',
        icon: <MdGroupAdd size="24" />,
        onSelect: () => void navigate(RouteNames.USERS_ACTIVATION),
    });
    const setMailDeputyItem = userMenuItem({
        value: 'mail-deputy',
        label: 'Замещение сотрудников и уведомления',
        icon: <MdSupervisorAccount size="24" />,
        onSelect: () => void navigate(RouteNames.REDIRECTS_AND_REPLACEMENTS),
    });
    const setAssistantSimple = userMenuItem({
        value: 'set-assistant',
        label: 'Назначить заместителя',
        icon: <MdSupervisorAccount size="24" />,
        onSelect: () => void navigate(RouteNames.SET_ASSISTANT_SIMPLE),
    });
    const viewAccessGroupsItem = userMenuItem({
        value: 'roles',
        label: 'Мои роли и ответственность',
        icon: <MdOutlineAssignmentInd size="24" />,
        onSelect: () => void navigate(RouteNames.ROLES),
    });
    const notificationSettingsItem = userMenuItem({
        value: 'notification-settings',
        label: 'Настройка уведомлений',
        icon: <MdEditNotifications size="24" />,
        onSelect: () => void navigate(RouteNames.SETTINGS_NOTIFICATIONS),
    });

    const getCustomUserSettingsItem = (settings: ICustomUserSettings) =>
        userMenuItem({
            value: settings.formName,
            label: settings.title,
            icon: <MdSettings size="24" />,
            onSelect: () => void navigate(RouteNames.CUSTOMUSERSETTINGS.replace(':id', settings.id)),
            testId: false,
        });

    const logoutFromProfile = () => {
        // Токена нет или он битый (ручное удаление в DevTools, гонка с выходом другой вкладки) — способа входа нет,
        // обычный выход: кнопка Выхода работает всегда.
        if (auth.getSignInMethod() === 'SAML') {
            void navigate(RouteNames.ADFS_SAML_SSO_SIGN_OUT_REDIRECT, {
                replace: true,
            });
        } else {
            // Уводим на чистый /login ДО dispatch(logout): иначе ProtectedRoute
            // среагирует на isAuth=false и подложит ?returnUrl= из текущего URL.
            void navigate(RouteNames.LOGIN, { replace: true });
            logout();
        }
    };

    // Экран «Личные настройки»: маршруты настроек и кастомные формы конфигурации.
    // Прокрутка таблиц, размер текста и отладка живут внутри самого экрана.
    const personalItems: IListElement[] = [];
    if (user.canView.settings) personalItems.push(settingsItem);
    if (user.canView.settingsNotifications) personalItems.push(notificationSettingsItem);
    if (user.customUserSettings && user.customUserSettings.length > 0) {
        user.customUserSettings.forEach((item) => personalItems.push(getCustomUserSettingsItem(item)));
    }

    // Экран «Управление доступом»: администрирование пользователей и ролей.
    const accessItems: IListElement[] = [];
    if (user.canView.usersActivation) accessItems.push(usersActivationItem);
    if (user.canView.accessGroups) accessItems.push(viewAccessGroupsItem);

    // Экран «Замещения»: делегирование уведомлений и назначение заместителей.
    const deputyItems: IListElement[] = [];
    if (user.canView.mailDeputy) deputyItems.push(setMailDeputyItem);
    if (user.canView.setAssistantSimple) deputyItems.push(setAssistantSimple);

    const onChatAIToggle = useCallback(() => {
        setChatAIState((prev) => (prev === 'expanded' ? 'closed' : 'expanded'));
    }, []);
    const onChatAIMinimize = useCallback(() => setChatAIState('minimized'), []);
    const onChatAIExpand = useCallback(() => setChatAIState('expanded'), []);
    const onChatAIClose = useCallback(() => setChatAIState('closed'), []);

    const { status: chatAIStatus, notConfigured: chatAINotConfigured } = useChatAIStatus({
        enabled: chatAIState !== 'closed',
    });

    // -------------------------------------------------------------------------------------------------------------------
    // Отладочные настройки
    // -------------------------------------------------------------------------------------------------------------------

    const [debugSettings, setDebugSettings] = useLocalStorageState<IDebugSettings>('Debug-Settings', {
        notifyFormulaErrors: config.environment !== 'PROD',
        showActionKeys: config.environment !== 'PROD',
        disableUnsavedChangesGuard: false,
    });

    useEffect(() => {
        setDebugSettings(debugSettings);
    }, []);

    const onChangeDebugSettings = useCallback(
        (value: IDebugSettings) => {
            setDebugSettings(value);
        },
        [setDebugSettings],
    );

    // -------------------------------------------------------------------------------------------------------------------

    const chatAIEnabled = config.application.AI?.chatAIEnabled;
    const agentAIEnabled = config.application.AI?.agentAIEnabled;
    const chatAIHint = config.application.AI?.chatAIHint;
    const chatAITitle = config.application.AI?.chatAITitle;
    const currentUser = useTypedSelector((state) => state.auth.user);
    const [chatAIHintDismissed, setChatAIHintDismissed] = useLocalStorageState<boolean>(
        `user-${currentUser.username}.chatAIHint`,
        false,
    );
    // listMenuChat больше не нужен: переключатель режимов (RAG / Agent) переехал внутрь
    // ChatAIForm как ChatAIModeSelector. Navbar теперь открывает форму одной кнопкой —
    // mode читается из slice (default + localStorage) и меняется юзером в шапке формы.

    const chatAIName = chatAITitle || 'Цифровой помощник';
    const chatAIButtonRef = useRef<HTMLButtonElement>(null);

    const chatAITooltipContent = !chatAIHintDismissed ? (
        <div className="chat-ai__tooltip-content">
            <span className="chat-ai__tooltip-spark chat-ai__tooltip-spark--a" aria-hidden="true">
                <MdAISparkles size="14" />
            </span>
            <span className="chat-ai__tooltip-spark chat-ai__tooltip-spark--b" aria-hidden="true">
                <MdAISparkles size="10" />
            </span>
            <div className="chat-ai__tooltip-header">
                <span className="chat-ai__tooltip-badge" aria-hidden="true">
                    <AiBotIcon size={32} />
                </span>
                <span className="chat-ai__tooltip-title">{chatAIName}</span>
            </div>
            <div className="chat-ai__tooltip-text">
                {chatAIHint ||
                    'Ваш помощник по системе: объясню функции, подскажу шаги выполнения операций и помогу найти нужную информацию из Базы знаний.'}
            </div>
            <div className="chat-ai__tooltip-dismiss-row">
                <Button
                    variant="primary"
                    tone="neutral"
                    size="sm"
                    className="chat-ai__tooltip-dismiss"
                    onClick={(event) => {
                        // промо закрывается вместе с кнопкой — фокус с неё возвращаем на кнопку помощника
                        if (event.currentTarget.contains(document.activeElement)) chatAIButtonRef.current?.focus();
                        setChatAIHintDismissed(true);
                    }}
                >
                    Понятно
                </Button>
            </div>
        </div>
    ) : null;

    showNotificationDropdownRef.current = showNotificationDropdown;

    const avatarHint =
        activeHint === 'theme'
            ? {
                  icon: <MdDarkMode size={17} />,
                  title: 'Появился переключатель темы',
                  text: 'Откройте меню профиля, чтобы выбрать светлую или тёмную тему',
                  onActivate: () => setShowMenu(true),
                  onClose: dismissThemeHint,
              }
            : {
                  icon: <MdAddAPhoto size={17} />,
                  title: 'Установите аватар',
                  text: 'Откройте меню, чтобы добавить фото профиля',
                  onActivate: openAvatarEditor,
                  onClose: dismissAvatarHint,
              };

    return (
        <Header
            brand={
                <HeaderBrand to="/" data-testid="navbar-logo">
                    <img src={logo} alt="logo" />
                </HeaderBrand>
            }
            search={<GlobalSearchInput />}
            // Глобальных целей в шапке кроме поиска ровно четыре: помощник, «Помощь», уведомления и профиль.
            // Настройки, документация, новости релиза, поддержка и отладка живут внутри «Помощи» и меню профиля.
            // Админам и вне PROD к ним добавляются «Инструменты» — переходы в админку, интеграции и т. п.
            actions={
                <>
                    {(chatAIEnabled || agentAIEnabled) && (
                        <div className="chat-ai-button-wrapper">
                            <Tooltip
                                className="chat-ai-button-tooltip"
                                openDelay={100}
                                position="bottom"
                                hidePadding={true}
                                offsetValue={{ mainAxis: NAVBAR_DROPDOWN_VERTICAL_OFFSET, crossAxis: 0 }}
                                label={chatAITooltipContent}
                            >
                                <Button
                                    ref={chatAIButtonRef}
                                    variant="header"
                                    className="chat-ai-button"
                                    size="md"
                                    aria-label={chatAIName}
                                    startAdornment={<AiBotIcon size={32} />}
                                    onClick={onChatAIToggle}
                                />
                            </Tooltip>
                        </div>
                    )}
                    {canSeeDebugTools(user.canView.adminPanel) && <ToolsMenu />}
                    <HelpMenu
                        onOpenChange={onHelpMenuOpenChange}
                        screenShareHint={
                            hintOpen && activeHint === 'screenShare' && activeStep !== 'done' ? activeStep : undefined
                        }
                        onScreenShareHintDone={dismissScreenShareHint}
                    />
                    <BellJobHint
                        arrival={bellLanding.hint}
                        onActivate={openFromBellHint}
                        onClose={bellLanding.closeHint}
                    >
                        <div ref={notificationButtonElement} className="notification-dialog-anchor">
                            <NotificationDropdown
                                target={
                                    <Button
                                        variant="header"
                                        title="Уведомления"
                                        aria-label={bellLabel(unreadCount, hasRunningJob, bellJobFx.failureMark)}
                                        data-testid="notifications-button"
                                        size="md"
                                        startAdornment={
                                            <NotificationBell
                                                unreadCount={unreadCount}
                                                jobRunning={hasRunningJob}
                                                ringing={isBellRinging}
                                                ringingKey={bellAnimationKey}
                                                jobScene={bellJobFx.scene}
                                                failureMark={bellJobFx.failureMark}
                                            />
                                        }
                                        onClick={() => {
                                            clearToasts();
                                            setDropdownDetailRequest(null);
                                            setShowNotificationDropdown((prev) => {
                                                // При подключённом SignalR бейдж уже актуален (live-эхо) — на открытии
                                                // не дёргаем лишний раз; при обрыве канала — синхронизируем счётчик.
                                                if (!prev && !notificationHub.isConnected) {
                                                    fetchUnreadCount();
                                                }

                                                return !prev;
                                            });
                                        }}
                                    />
                                }
                                opened={showNotificationDropdown}
                                onClose={() => {
                                    setShowNotificationDropdown(false);
                                    setDropdownDetailRequest(null);
                                }}
                                unreadCount={unreadCount}
                                refreshSignal={dropdownRefreshSignal}
                                onLocalMutation={handleLocalMutation}
                                detailRequest={dropdownDetailRequest}
                                onJobDismissed={bellJobFx.forgetFailure}
                            />
                            <NotificationToast
                                items={toastItems}
                                onOpen={handleToastOpen}
                                onMarkRead={handleToastMarkRead}
                                onSnoozePreset={handleToastSnoozePreset}
                                onSnoozeCustom={handleToastSnoozeCustom}
                                onDismiss={removeToast}
                                onHoverChange={handleToastHover}
                                onShowAll={handleToastShowAll}
                                onJobAction={handleToastJobAction}
                                downloadingJobIds={jobDownloads}
                            />
                            <JobLiveAnnouncer />
                            {snoozeCustom && (
                                <React.Suspense fallback={null}>
                                    <MailReminderExecutorModal
                                        docId={snoozeCustom.docKey}
                                        title="Отложить уведомление"
                                        modalSize="xl"
                                        actionKey="78cd3077-3c5a-4cd8-b6f5-9b4780026697"
                                        okButtonText="ОК"
                                        cancelButtonText="Отмена"
                                        prefilledText={snoozeCustom.text}
                                        completeHandler={() => setSnoozeCustom(null)}
                                    />
                                </React.Suspense>
                            )}
                        </div>
                    </BellJobHint>
                </>
            }
            userMenu={
                <UserMenuDialog
                    target={
                        <div ref={userMenuButtonElement} className="user-menu-anchor">
                            <OnboardingHint
                                opened={hintOpen && activeHint !== 'screenShare' && activeStep === 0}
                                position="bottom"
                                arrowPosition="center"
                                offset={10}
                                {...avatarHint}
                            >
                                <UserMenuButton
                                    expanded={showMenu}
                                    onClick={toggleUserMenu}
                                    name={user.name}
                                    avatar={
                                        <UserAvatar
                                            userKey={String(user.id)}
                                            name={user.fullName}
                                            hasImage={user.hasImage}
                                            version={effectiveAvatarVersion}
                                            size={AVATAR_SIZES.m}
                                        />
                                    }
                                />
                            </OnboardingHint>
                        </div>
                    }
                    opened={showMenu}
                    personalItems={personalItems}
                    accessItems={accessItems}
                    deputyItems={deputyItems}
                    onLogout={user.canView.signOut ? logoutFromProfile : undefined}
                    debugSettings={config.environment !== 'PROD' ? debugSettings : undefined}
                    onChangeDebugSettings={config.environment !== 'PROD' ? onChangeDebugSettings : undefined}
                    onClose={closeUserMenu}
                    onOrgChange={() => {
                        setIsOrgEditMode(true);
                    }}
                    onEditAvatar={openAvatarEditor}
                    onDismissHint={dismissAvatarHint}
                    avatarHintOpen={hintOpen && activeHint === 'avatar' && activeStep === 1}
                    onDismissThemeHint={dismissThemeHint}
                    themeHintOpen={hintOpen && activeHint === 'theme' && activeStep === 1}
                />
            }
        >
            {chatAIState !== 'closed' && (
                <ChatAIForm
                    isMinimized={chatAIState === 'minimized'}
                    status={chatAIStatus}
                    notConfigured={chatAINotConfigured}
                    onClose={onChatAIClose}
                    onMinimize={onChatAIMinimize}
                    onExpand={onChatAIExpand}
                />
            )}

            <AvatarEditorModal
                opened={avatarEditorOpen}
                onClose={() => setAvatarEditorOpen(false)}
                onSave={handleSaveAvatar}
                onRemove={handleRemoveAvatar}
                hasExisting={user.hasImage}
                currentSrc={currentAvatarSrc}
            />

            {isOrgEditMode &&
                (isSavingDepartment ? (
                    <Preloader size="xl" />
                ) : (
                    <Modal
                        size="m"
                        onClose={() => {
                            setIsOrgEditMode(false);
                        }}
                        header="Выбор организации"
                        hasUnsavedInput={() => departmentsList.length > 0 && selectedDep?.value !== shownDepRef.current}
                        footer={
                            <Button
                                disabled={!selectedDep}
                                onClick={async () => {
                                    if (selectedDep) {
                                        setIsSavingDepartment(true);
                                        await UserService.setUserDepartment(selectedDep.value);
                                        getCurrentUser(auth.getAccessToken() ?? '');
                                        setIsOrgEditMode(false);
                                        setIsSavingDepartment(false);
                                    }
                                }}
                            >
                                OK
                            </Button>
                        }
                    >
                        <Select
                            placeholder="Выберите организацию"
                            onlySelect
                            values={selectedDep ? [selectedDep] : undefined}
                            options={departmentsList}
                            loading={departmentsList.length == 0}
                            onChange={(e) => {
                                setSelectedDep(e?.length > 0 ? e[0] : undefined);
                            }}
                        />
                    </Modal>
                ))}
        </Header>
    );
};

export default Navbar;
